import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentProps, PropsWithChildren } from 'react';
import type React from 'react';

import type { IAllProps as EditorProps } from '@hugerte/hugerte-react';
import { Editor } from '@hugerte/hugerte-react';
import type { Editor as EditorType } from 'hugerte';

import { Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { ClientRender } from '@change-corgi/core-react/ssr/render';

import localeMapping from '#config/hugerte/locale-mapping.json';

import { isDarkModeApplied } from '#src/app/shared/hooks/dom';
import { useLocale } from '#src/app/shared/hooks/l10n';

import { useDefaultConfig } from './hooks/useDefaultConfig';
import { hugerte } from './hugerte';
import { GlobalStyle } from './HugeRteStyling';
import { attachExitOnEscape } from './shared/attachExitOnEscape';
import { filePickerCallback } from './shared/config';
import { fixHelpAriaText } from './shared/fixHelpAriaText';

type EditorInitConfig = EditorProps['init'];

export type Props = {
	ariaLabel: string;
	customEditorConfig?: EditorInitConfig;
	descriptiveLabel: string;
	hideLabel?: boolean;
	textEditorId: string;
	initialValue?: string;
	onEditorChange?: (editorContent: string, editor: EditorType) => void;
	onEditorFocus?: () => void;
	onEditorInit?: (editor: EditorType) => void;
	// Scopes opt-in `.tox-*` overrides (see HugeRteStyling.tsx) to this instance only, instead of
	// applying them to every RichTextEditor in the app.
	wrapperClassName?: string;
} & ComponentProps<typeof Editor>;

// Only wraps when a consumer opts into scoped styling — every other consumer keeps the pre-existing
// DOM shape (Editor as a direct sibling), so this doesn't insert an unstyled wrapper div into
// unrelated layouts. Kept outside RichTextEditorInner to keep that function under the
// max-lines-per-function CI gate.
function withOptionalWrapper(className: string | undefined, children: React.ReactNode): React.ReactNode {
	return className ? <div className={className}>{children}</div> : children;
}

const RichTextEditorInner: React.FC<PropsWithChildren<Props>> = ({
	ariaLabel,
	customEditorConfig,
	descriptiveLabel,
	hideLabel,
	textEditorId,
	initialValue,
	onEditorChange,
	onEditorFocus,
	onEditorInit,
	textareaName,
	wrapperClassName,
	...rest
}) => {
	const editorRef = useRef<EditorType | null>(null);
	const locale = useLocale();
	const localeCode = localeMapping.localeMapping[locale];
	// useIsDarkMode() deliberately returns false on the first render and only corrects itself in
	// an effect, to avoid SSR hydration mismatches — but this component only ever renders
	// client-side (see ClientRender below), so that safety net isn't needed here, and it actively
	// hurts: the <Editor key={isDark ? ...}> below remounts on every isDark change, so that
	// harmless startup correction (false -> true when dark mode is actually active) was racing
	// with HugeRte's own async init and could leave the editor silently unrendered.
	const [isDark, setIsDark] = useState(isDarkModeApplied);
	useEffect(() => {
		const observer = new MutationObserver(() => setIsDark(isDarkModeApplied()));
		observer.observe(document.documentElement, { attributeFilter: ['class'] });
		return () => observer.disconnect();
	}, []);

	const defaultConfig = useDefaultConfig(isDark);

	const editorConfig: EditorInitConfig = useMemo(
		() => ({
			...defaultConfig,
			...customEditorConfig,
			content_style: `${defaultConfig?.content_style || ''}\n${customEditorConfig?.content_style || ''}`,
			// eslint-disable-next-line react-hooks/refs
			file_picker_callback: filePickerCallback(editorRef),
		}),
		[defaultConfig, customEditorConfig],
	);

	const [editorText, setEditorText] = useState(initialValue ?? '');
	const [hasBeenInitialized, setHasBeenInitialized] = useState(false);

	const handleEditorChange = useCallback(
		(newValue: string, editor: EditorType) => {
			setEditorText(newValue);
			/*
				HugeRte makes some formatting to the editor's content during initialization which triggers
				a change event. We want to set the editor as dirty only when the content has been changed by
				the user (usually after the editor is initialized)
			*/
			if (hasBeenInitialized) {
				editor.setDirty(true);
			}

			onEditorChange?.(newValue, editor);
		},
		[onEditorChange, hasBeenInitialized],
	);

	const handleEditorFocus = useCallback(() => {
		onEditorFocus?.();
	}, [onEditorFocus]);

	const handleEditorInit = useCallback(
		(_evt: unknown, editor: EditorType) => {
			editorRef.current = editor;
			hugerte.util.I18n.add(localeCode, { URL: 'URL' });
			attachExitOnEscape(editor);
			fixHelpAriaText(editor);
			onEditorInit?.(editor);
			setHasBeenInitialized(true);

			// HugeRte appends aux UI containers (dropdowns, menus, dialogs) to document.body.
			// React Aria marks body-level elements as inert when a modal is open (ariaHideOutside
			// with shouldUseInert:true). data-react-aria-top-layer opts them out — same fix as Toast.
			for (const el of Array.from(document.body.children)) {
				if (el instanceof HTMLElement && /\btox\b/.test(el.className)) {
					el.setAttribute('data-react-aria-top-layer', 'true');
					el.inert = false;
				}
			}
		},
		[localeCode, onEditorInit],
	);

	const handleLabelClick = useCallback(() => {
		editorRef.current?.focus();
	}, [editorRef]);

	return (
		<>
			<GlobalStyle />
			{/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
			<div className={cn('mb-1', { 'sr-only': hideLabel })} onClick={handleLabelClick}>
				<label htmlFor={textEditorId}>
					<Text as="span" size="x-small" fontWeight="bold">
						{descriptiveLabel}
					</Text>
				</label>
			</div>
			{withOptionalWrapper(
				wrapperClassName,
				<Editor
					key={isDark ? 'dark' : 'light'}
					onInit={handleEditorInit}
					init={editorConfig}
					id={textEditorId}
					initialValue={initialValue}
					value={editorText}
					onEditorChange={handleEditorChange}
					onFocus={handleEditorFocus}
					data-testid={textEditorId}
					aria-label={ariaLabel}
					textareaName={textareaName}
					{...rest}
				/>,
			)}
		</>
	);
};

export const RichTextEditor: React.FC<PropsWithChildren<Props>> = (props) => {
	return (
		// SSR not supported by HugeRte
		<ClientRender>
			<RichTextEditorInner {...props} />
		</ClientRender>
	);
};
